import React, { useCallback, useContext, useEffect, useState } from "react";
import { fetchQuery, graphql, useLazyLoadQuery } from "react-relay";

import { faCheck, faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import cx from "classnames";

import { AppContext } from "~/common";
import { DESKTOP_BOTLESS_RECORDING_FEATURE_FLAG } from "~/meetingRecorder/featureFlag";
import { environment } from "~/models";
import { Button, useModals } from "~/ui";
import { IconButton, Modal } from "~/ui/legacyFellowIndex";
import toasty from "~/ui/toast";

import { commitMutationPromise } from "../relayUtil";
import { dispatchAnalyticsTrack } from "../segment";
import botlessRecordingIllustration from "./BotlessRecordingIllustration.png";

import { type desktopRecorderApiCanEventBeLocallyRecordedQuery } from "~/__generated__/desktopRecorderApiCanEventBeLocallyRecordedQuery.graphql";
import { type desktopRecorderApiDesktopRecorderQuery } from "~/__generated__/desktopRecorderApiDesktopRecorderQuery.graphql";
import type { desktopRecorderApiGetUploadLinksMutation } from "~/__generated__/desktopRecorderApiGetUploadLinksMutation.graphql";
import { type desktopRecorderApiPauseLocalRecordingMutation } from "~/__generated__/desktopRecorderApiPauseLocalRecordingMutation.graphql";
import { type desktopRecorderApiRecorderEndLocalRecordingMutation as recorderEndLocalRecordingMutation } from "~/__generated__/desktopRecorderApiRecorderEndLocalRecordingMutation.graphql";
import { type desktopRecorderApiRecorderStartLocalRecordingMutation as recorderStartLocalRecordingMutation } from "~/__generated__/desktopRecorderApiRecorderStartLocalRecordingMutation.graphql";
import { type desktopRecorderApiResumeLocalRecordingMutation } from "~/__generated__/desktopRecorderApiResumeLocalRecordingMutation.graphql";

import styles from "./RequestRecordingAccesses.module.css";

const windowEmitter = new EventTarget();

const desktopRecorderQuery = graphql`
	query desktopRecorderApiDesktopRecorderQuery {
		account {
			allowBotlessRecording
		}
	}
`;

const startLocalRecordingMutation = graphql`
	mutation desktopRecorderApiRecorderStartLocalRecordingMutation($eventId: ID, $noteId: ID) {
		createLocalRecording(eventId: $eventId, noteId: $noteId) {
			meetingRecording {
				id
				note {
					... on Note {
						id
						url
					}
				}
			}
		}
	}
`;

const endLocalRecordingMutation = graphql`
	mutation desktopRecorderApiRecorderEndLocalRecordingMutation($meetingRecordingId: ID!, $audioUrl: String!) {
		endLocalRecording(meetingRecordingId: $meetingRecordingId, audioUrl: $audioUrl) {
			ok
		}
	}
`;

const getUploadLinksMutation = graphql`
	mutation desktopRecorderApiGetUploadLinksMutation($meetingRecordingId: ID!, $audioFileExtension: String!) {
		getLocalRecordingUploadLinks(meetingRecordingId: $meetingRecordingId, audioFileExtension: $audioFileExtension) {
			audioUrl
			audioFields
		}
	}
`;

const pauseLocalRecordingMutation = graphql`
	mutation desktopRecorderApiPauseLocalRecordingMutation($meetingRecordingId: ID!) {
		confirmClientSideRecordingDidPause(meetingRecordingId: $meetingRecordingId) {
			ok
		}
	}
`;

const resumeLocalRecordingMutation = graphql`
	mutation desktopRecorderApiResumeLocalRecordingMutation($meetingRecordingId: ID!) {
		confirmClientSideRecordingDidResume(meetingRecordingId: $meetingRecordingId) {
			ok
		}
	}
`;

const canEventBeLocallyRecordedQuery = graphql`
	query desktopRecorderApiCanEventBeLocallyRecordedQuery($eventId: ID!) {
		event: node(id: $eventId) {
			... on Event {
				canBeLocallyRecorded
			}
		}
	}
`;

const RECORDING_ACCESS_TIMES_SHOWED_KEY = "RECORDING_ACCESS_TIMES_SHOWED";
const MAX_TIMES_TO_SHOW = 3;
const FIRST_SCREEN_RECORDING_REQUESTED = "FIRST_SCREEN_RECORDING_REQUESTED";

interface DeniedAccessPopupProps {
	settingName: string;
	settingPath: string;
	onOpenSystemSettings: () => void;
}

function DeniedAccessPopup({ settingName, settingPath, onOpenSystemSettings }: DeniedAccessPopupProps) {
	return (
		<div className={styles.deniedAccessPopup}>
			<small>{settingName} permission was not granted</small>
			Please open{" "}
			<a onClick={onOpenSystemSettings} className={styles.settingsLink}>
				System Settings &gt; Privacy & Security &gt; {settingPath}
			</a>
			, and allow Fellow to use it.
			<a
				href="https://help.fellow.ai/en/articles/12015496-recording-with-the-desktop-app"
				target="_blank"
				rel="noreferrer"
			>
				{" "}
				Learn more.
			</a>
		</div>
	);
}

interface RequestRecordingAccessProps {
	title: string;
	iconText: string;
	buttonText: string;
	successText: string;
	onClick: () => void;
	granted: boolean;
}

function RequestRecordingAccess({
	title,
	iconText,
	buttonText,
	successText,
	onClick,
	granted,
}: RequestRecordingAccessProps) {
	return (
		<div className={styles.access}>
			<span className={cx(styles.preIcon, { [styles.greenIcon]: granted })}>
				{granted ? <FontAwesomeIcon icon={faCheck} /> : iconText}
			</span>
			{title}
			{granted ? (
				<span className={styles.success}>{successText}</span>
			) : (
				<Button variant="primary" size="slim" onClick={onClick}>
					{buttonText}
				</Button>
			)}
		</div>
	);
}

interface RequestRecordingAccessesProps {
	onClose: () => void;
}

function RequestRecordingAccesses({ onClose }: RequestRecordingAccessesProps) {
	const [microphoneAccess, setMicrophoneAccess] = useState(false);
	const [screenRecordingAccess, setScreenRecordingAccess] = useState(false);

	const updateAccesses = useCallback(async () => {
		const { granted: microphoneAccess } = await isMicrophoneAccessible();
		const { granted: screenRecordingAccess } = await isScreenRecordingAccessible();
		setMicrophoneAccess(microphoneAccess);
		setScreenRecordingAccess(screenRecordingAccess);
	}, []);

	const onEnableMicrophone = useCallback(async () => {
		const { granted } = await requestMicrophoneAccess();
		setMicrophoneAccess(granted);
		dispatchAnalyticsTrack("Recording Access Permission Actioned", {
			feature: "meeting recorder",
			permission: "microphone",
			granted,
		});
		if (!granted) {
			toasty.error(
				<DeniedAccessPopup
					settingName="Microphone"
					settingPath="Microphone"
					onOpenSystemSettings={openMicrophoneSettings}
				/>,
			);
		}
	}, []);

	const onEnableScreenRecording = useCallback(async () => {
		const { granted } = await requestScreenRecordingAccess();
		setScreenRecordingAccess(granted);
		dispatchAnalyticsTrack("Recording Access Permission Actioned", {
			feature: "meeting recorder",
			permission: "screen recording",
			granted,
		});
		// Unfortunately the MacOS API for request screen recording will always return False
		// Even if this is the first time that we are requesting and the popup will shown
		// Other permissions like the Microphone will wait for the user to accept/deny the popup
		// Before returning an result, but this is not the case here. I've tried a few different APIs
		// For request access using Cursor's help but none of them worked. So we will force to not show
		// The toast if this is the first time that we get a denial
		if (!granted && localStorage.getItem(FIRST_SCREEN_RECORDING_REQUESTED)) {
			localStorage.setItem(FIRST_SCREEN_RECORDING_REQUESTED, "true");
			toasty.error(
				<DeniedAccessPopup
					settingName="Screen Recording"
					settingPath="Screen & System Audio Recording"
					onOpenSystemSettings={openScreenRecordingSettings}
				/>,
			);
		}
	}, []);

	const onFinish = useCallback(() => {
		onClose();
		dispatchAnalyticsTrack("Recording Access Modal Finished", {
			feature: "meeting recorder",
		});
	}, [onClose]);

	const onDismiss = useCallback(() => {
		onClose();
		dispatchAnalyticsTrack("Recording Access Modal Dismissed", { feature: "meeting recorder" });
	}, [onClose]);

	useEffect(() => {
		updateAccesses();
	}, [updateAccesses]);

	return (
		<Modal onClose={onDismiss} className={styles.modal}>
			<Modal.Body className={styles.body}>
				<div className={styles.content}>
					<div>
						<h2 className={styles.title}>
							Capture your meetings on any platform <span className={styles.highlight}>without a bot</span> on the call
						</h2>
						<p className={styles.description}>
							Enable your bot-free recording option by providing your microphone and screen recording permissions.
						</p>
					</div>
					<div className={styles.accesses}>
						<RequestRecordingAccess
							title="Transcribe my voice"
							buttonText="Enable Microphone"
							iconText="1"
							successText="Microphone enabled"
							onClick={onEnableMicrophone}
							granted={microphoneAccess}
						/>
						<RequestRecordingAccess
							title="Transcribe other attendee voices"
							buttonText="Enable Screen Recording"
							iconText="2"
							successText="Screen Recording enabled"
							onClick={onEnableScreenRecording}
							granted={screenRecordingAccess}
						/>
					</div>
					<div className={styles.actions}>
						<Button variant="primary" onClick={onFinish} disabled={!microphoneAccess || !screenRecordingAccess}>
							Continue
						</Button>
						<button className={styles.dismissButton} onClick={onDismiss}>
							Not now
						</button>
					</div>
				</div>
				<img src={botlessRecordingIllustration} className={styles.image} />
				{/* This modal doesn't have a header and the body needs to occupy the whole height
				Of the modal, so we will need to add the close button manually here. TODO: We could include
				this feature in the Modal component */}
				<IconButton
					title="Close"
					icon={faTimes}
					className={styles.closeButton}
					onClick={onDismiss}
					data-test-id="modal-header-close-button"
				/>
			</Modal.Body>
		</Modal>
	);
}

export default function DesktopRecorderApi() {
	const data = useLazyLoadQuery<desktopRecorderApiDesktopRecorderQuery>(
		desktopRecorderQuery,
		{},
		{ fetchPolicy: "network-only" },
	);
	const { user } = useContext(AppContext);
	const modals = useModals();
	const isDesktopRecordingAvailable = useFeatureIsOn(DESKTOP_BOTLESS_RECORDING_FEATURE_FLAG);

	const openAccessModal = useCallback(() => {
		modals.open(({ close }) => <RequestRecordingAccesses onClose={close} />);
		dispatchAnalyticsTrack("Recording Access Modal Opened", { feature: "meeting recorder" });
		window.desktopAppApi?.openMainWindow();
	}, [modals]);

	const canDoBotlessRecording = useCallback(() => {
		return (data?.account?.allowBotlessRecording ?? false) && isDesktopRecordingAvailable;
	}, [data, isDesktopRecordingAvailable]);

	useEffect(() => {
		if (!window.desktopRecorder) return;
		window.desktopRecorder.registerCanDoBotlessRecording(canDoBotlessRecording);
	}, [canDoBotlessRecording]);

	useEffect(() => {
		// So we don't show it when the user is logged off or the feature flag is disabled
		if (!user || !isDesktopRecordingAvailable) return;
		// We should wait a little bit after this component is rendered to show this modal
		// TODO: We might want to make this better
		const timesShowed = parseInt(localStorage.getItem(RECORDING_ACCESS_TIMES_SHOWED_KEY) ?? "0");
		if (timesShowed > MAX_TIMES_TO_SHOW) return;
		async function showAccessModal() {
			// This should never happen but just in case if the first time we would show this modal
			// The user already gave us access
			const { granted: microphoneAccessible } = await isMicrophoneAccessible();
			const { granted: screenRecordingAccessible } = await isScreenRecordingAccessible();
			if (microphoneAccessible && screenRecordingAccessible) return;
			localStorage.setItem(RECORDING_ACCESS_TIMES_SHOWED_KEY, (timesShowed + 1).toString());
			setTimeout(openAccessModal, 5000);
		}
		showAccessModal();
	}, [user, openAccessModal, isDesktopRecordingAvailable]);

	useEffect(() => {
		windowEmitter.addEventListener("requestRecordingAccess", openAccessModal);
		return () => {
			windowEmitter.removeEventListener("requestRecordingAccess", openAccessModal);
		};
	}, [openAccessModal]);

	return <></>;
}

export async function startLocalRecording(eventId?: string, noteId?: string) {
	const { granted: hasMicrophoneAccess } = await isMicrophoneAccessible();
	const { granted: hasScreenRecordingAccess } = await isScreenRecordingAccessible();
	if (!hasMicrophoneAccess || !hasScreenRecordingAccess) {
		windowEmitter.dispatchEvent(new CustomEvent("requestRecordingAccess"));
		return null;
	}
	return commitMutationPromise<recorderStartLocalRecordingMutation>(environment, {
		mutation: startLocalRecordingMutation,
		variables: {
			eventId,
			noteId,
		},
	}).then(response => {
		return response.createLocalRecording?.meetingRecording;
	});
}

export async function getUploadLinks(meetingRecordingId: string, audioFileExtension: string) {
	return commitMutationPromise<desktopRecorderApiGetUploadLinksMutation>(environment, {
		mutation: getUploadLinksMutation,
		variables: {
			meetingRecordingId,
			audioFileExtension,
		},
	}).then(response => {
		return response.getLocalRecordingUploadLinks;
	});
}

function uploadFileToURL(file: File, uploadUrl: string, fields: Record<string, string>): Promise<void> {
	return new Promise<void>((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open("POST", uploadUrl, true);
		const formData = new FormData();
		for (const [key, value] of Object.entries(fields)) {
			formData.append(key, value);
		}
		formData.append("file", file);
		xhr.addEventListener("load", () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				resolve();
			} else {
				reject(new Error(`Failed to upload file: ${xhr.status} ${xhr.statusText}`));
			}
		});
		xhr.addEventListener("error", () => {
			reject(new Error("Failed to upload file"));
		});
		xhr.send(formData);
	});
}

export async function finishLocalRecording(meetingRecordingId: string, audioBuffer: Buffer) {
	const uploadLinks = await getUploadLinks(meetingRecordingId, "m4a");
	console.log("Upload links", uploadLinks);
	if (!uploadLinks) return false;
	const { audioUrl, audioFields } = uploadLinks;
	try {
		await uploadFileToURL(new File([audioBuffer], "audio.m4a"), audioUrl, audioFields);
	} catch (error) {
		console.error("Failed to upload files", error);
		return false;
	}
	return commitMutationPromise<recorderEndLocalRecordingMutation>(environment, {
		mutation: endLocalRecordingMutation,
		variables: {
			meetingRecordingId,
			audioUrl: audioFields.key,
		},
	}).then(response => {
		return response.endLocalRecording?.ok || false;
	});
}

export async function checkEventForLocalRecording(eventId: string) {
	return fetchQuery<desktopRecorderApiCanEventBeLocallyRecordedQuery>(environment, canEventBeLocallyRecordedQuery, {
		eventId,
	})
		.toPromise()
		.then(response => {
			return response?.event?.canBeLocallyRecorded ?? false;
		})
		.catch(error => {
			console.error("Failed to check event for local recording", error);
			return false;
		});
}

export async function propagatePauseRecording(meetingRecordingId: string) {
	return commitMutationPromise<desktopRecorderApiPauseLocalRecordingMutation>(environment, {
		mutation: pauseLocalRecordingMutation,
		variables: {
			meetingRecordingId,
		},
	}).then(response => {
		return response.confirmClientSideRecordingDidPause?.ok ?? false;
	});
}

export async function propagateResumeRecording(meetingRecordingId: string) {
	return commitMutationPromise<desktopRecorderApiResumeLocalRecordingMutation>(environment, {
		mutation: resumeLocalRecordingMutation,
		variables: {
			meetingRecordingId,
		},
	}).then(response => {
		return response.confirmClientSideRecordingDidResume?.ok ?? false;
	});
}

export async function isMicrophoneAccessible() {
	if (!window.desktopRecorder || !window.desktopRecorder.isMicrophoneAccessible) return { granted: false, ok: false };
	return window.desktopRecorder.isMicrophoneAccessible();
}

export async function isScreenRecordingAccessible() {
	if (!window.desktopRecorder || !window.desktopRecorder.isScreenRecordingAccessible)
		return { granted: false, ok: false };
	return window.desktopRecorder.isScreenRecordingAccessible();
}

export async function openMicrophoneSettings() {
	if (!window.desktopRecorder || !window.desktopRecorder.openMicrophoneSettings) return { granted: false, ok: false };
	return window.desktopRecorder.openMicrophoneSettings();
}

export async function openScreenRecordingSettings() {
	if (!window.desktopRecorder || !window.desktopRecorder.openScreenRecordingSettings)
		return { granted: false, ok: false };
	return window.desktopRecorder.openScreenRecordingSettings();
}

export async function requestMicrophoneAccess() {
	if (!window.desktopRecorder || !window.desktopRecorder.requestMicrophoneAccess) return { granted: false, ok: false };
	return window.desktopRecorder.requestMicrophoneAccess();
}

export async function requestScreenRecordingAccess() {
	if (!window.desktopRecorder || !window.desktopRecorder.requestScreenRecordingAccess)
		return { granted: false, ok: false };
	return window.desktopRecorder.requestScreenRecordingAccess();
}

/* Window.floatingNote check can go away once we stop using the full app for the floating note */
if (window.desktopRecorder && !Boolean(window.floatingNote)) {
	// These registrations can go away once everyone has updated to the latest desktop app
	// @ts-ignore
	window.desktopRecorder?.registerStartLocalRecording?.(startLocalRecording);
	// @ts-ignore
	window.desktopRecorder?.registerFinishLocalRecording?.(finishLocalRecording);
	// @ts-ignore
	window.desktopRecorder?.registerCheckEventForLocalRecording?.(checkEventForLocalRecording);

	window.InvokeWebAPI?.registerInvokeWebHandlers({
		"recorder.check_event_for_local_recording": checkEventForLocalRecording,
		"recorder.finish_local_recording": finishLocalRecording,
	});
}
